import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tempo-io/sui-dropdownmenu';
import { Delete, Edit, MoreVert } from '@tempo-io/sui-icons';
import { Textarea } from '@tempo-io/sui-textarea';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getElapsedText } from '../../utils/elapsed-time';
import { ClickAwayListener } from '../ClickAwayListener';
import { UserAvatar } from '../UserAvatar';
import { type GenericComment, LifecycleAction, type LifecycleActionType } from './types';

type CommentsListProps = {
  comments: GenericComment[];
  updateComment?: (comment: GenericComment) => void;
  handleLifecycleClick?: (commentId: number, action: LifecycleActionType) => void;
  canModerateAllPosts?: boolean;
  contactId?: number;
};

const HeaderAnnotation = ({
  children,
  dataTestId,
}: {
  children: string | React.ReactNode;
  dataTestId?: string;
}) => (
  <>
    <span
      className="size-1 rounded-full bg-text-neutral-secondary inline-block shrink-0"
      aria-hidden="true"
    />
    <span
      className="fs-exclude text-xs leading-relaxed font-semibold text-text-neutral-secondary"
      data-dd-action-name="FIM 3 Comment - subtext redacted"
      data-testid={dataTestId}
    >
      {children}
    </span>
  </>
);

export const CommentsList = ({
  comments,
  updateComment,
  handleLifecycleClick,
  canModerateAllPosts,
  contactId,
}: CommentsListProps) => {
  const { t } = useTranslation('translation');

  const [editingState, setEditingState] = useState<{
    id: number;
    value: string;
  }>();

  return (
    <div className="flex flex-col gap-2">
      {comments.map((comment, index) => {
        const isArchived = !!comment.archivedAt;
        const canModerateThisPost =
          canModerateAllPosts || (!!contactId && contactId === comment.contactId);
        const canOnlyModerateThisPost = !canModerateAllPosts && canModerateThisPost;

        return (
          <div
            key={comment.id}
            className="group flex flex-col gap-1 rounded-sm p-0.5"
            data-testid={`fim3-comment-${comment.displayName}-${index}`}
          >
            <div className="flex gap-1 items-center relative">
              <UserAvatar name={comment.displayName} email={comment.email} />
              <span
                className="fs-exclude text-sm leading-normal font-semibold"
                data-dd-action-name="FIM 3 Comment - user redacted"
                data-testid={`fim3-comment-${index}-name`}
              >
                {comment.displayName ?? t(($) => $.ideaManagement.general.anonymous)}
              </span>
              <HeaderAnnotation dataTestId={`fim3-comment-${index}-created-at`}>
                {getElapsedText(comment.createdAt, t)}
              </HeaderAnnotation>

              {canModerateAllPosts ? (
                <>
                  <HeaderAnnotation dataTestId={`fim3-comment-${index}-internal`}>
                    {isArchived
                      ? t(($) => $.common.archived)
                      : comment.internal
                        ? t(($) => $.ideaManagement.general.internal)
                        : t(($) => $.ideaManagement.general.public)}
                  </HeaderAnnotation>
                  <div className="grow flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t(($) => $.common.moreOptions)}
                          data-testid={`fim3-comment-${index}-menu`}
                          disabled={!!editingState}
                        >
                          <MoreVert className="size-6 text-icon-neutral-secondary" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {!isArchived && (
                          <DropdownMenuItem
                            onClick={() => {
                              updateComment?.({
                                ...comment,
                                internal: !comment.internal,
                              });
                            }}
                          >
                            {comment.internal
                              ? t(($) => $.ideaManagement.general.publishToPortal)
                              : t(($) => $.ideaManagement.general.removeFromPublicPortal)}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            setEditingState({
                              id: comment.id,
                              value: comment.comment,
                            });
                          }}
                        >
                          {t(($) => $.ideaManagement.general.editComment)}
                        </DropdownMenuItem>
                        {!isArchived && !!handleLifecycleClick && (
                          <DropdownMenuItem
                            data-testid="fim3-feedback-title-area-archive-feedback"
                            onClick={() => {
                              handleLifecycleClick(comment.id, LifecycleAction.Archive);
                            }}
                          >
                            {t(($) => $.ideaManagement.general.archiveEntity, {
                              entityLabel: t(($) => $.ideaManagement.general.comment),
                            })}
                          </DropdownMenuItem>
                        )}
                        {isArchived && !!handleLifecycleClick && (
                          <DropdownMenuItem
                            data-testid="fim3-feedback-title-area-restore-feedback"
                            onClick={() => {
                              handleLifecycleClick(comment.id, LifecycleAction.Restore);
                            }}
                          >
                            {t(($) => $.ideaManagement.general.restoreEntity, {
                              entityLabel: t(($) => $.ideaManagement.general.comment),
                            })}
                          </DropdownMenuItem>
                        )}
                        {isArchived && !!handleLifecycleClick && (
                          <DropdownMenuItem
                            data-testid="fim3-feedback-title-area-delete-feedback"
                            onClick={() => {
                              handleLifecycleClick(comment.id, LifecycleAction.Delete);
                            }}
                          >
                            {t(($) => $.ideaManagement.general.deleteEntity, {
                              entityLabel: t(($) => $.ideaManagement.general.comment),
                            })}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </>
              ) : canModerateThisPost ? (
                <div className="grow flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t(($) => $.common.edit)}
                    className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
                    onClick={() =>
                      setEditingState({
                        id: comment.id,
                        value: comment.comment,
                      })
                    }
                    disabled={!!editingState}
                  >
                    <Edit className="size-6 text-icon-neutral-secondary" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t(($) => $.common.delete)}
                    className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
                    onClick={() => handleLifecycleClick?.(comment.id, LifecycleAction.Delete)}
                    disabled={!!editingState}
                  >
                    <Delete className="size-6 text-icon-neutral-secondary" />
                  </Button>
                </div>
              ) : comment.internal ? (
                <HeaderAnnotation dataTestId={`fim3-comment-${index}-internal`}>
                  {t(($) => $.ideaManagement.general.internal)}
                </HeaderAnnotation>
              ) : null}
            </div>
            {editingState && editingState.id === comment.id ? (
              <ClickAwayListener onClickAway={() => setEditingState(undefined)}>
                <div className="flex flex-col gap-1">
                  <Textarea
                    className="fs-exclude"
                    aria-label={t(($) => $.ideaManagement.general.editComment)}
                    data-dd-action-name="FIM 3 Comment - input redacted"
                    defaultValue={comment.comment}
                    id={`edit-a-comment${index}`}
                    rows={3}
                    onChange={(e) => {
                      setEditingState({
                        id: comment.id,
                        value: e.target.value,
                      });
                    }}
                  />
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditingState(undefined);
                      }}
                    >
                      {t(($) => $.common.cancel)}
                    </Button>
                    <Button
                      // The API rejects a blank comment, and this dialog closes without surfacing
                      // the error, so an empty save would look like it silently reverted.
                      disabled={!editingState.value.trim()}
                      onClick={() => {
                        if (editingState === undefined || updateComment === undefined) return;
                        updateComment({
                          ...comment,
                          comment: editingState.value ?? '',
                        });
                        setEditingState(undefined);
                      }}
                      data-testid={`fim3-comment-${index}-save-button`}
                    >
                      {t(($) => $.ideaManagement.general.update)}
                    </Button>
                  </div>
                </div>
              </ClickAwayListener>
            ) : (
              <p
                className={cn(
                  'fs-exclude ml-3 whitespace-pre-wrap text-sm leading-normal [overflow-wrap:anywhere]',
                  canOnlyModerateThisPost && 'cursor-pointer',
                )}
                data-dd-action-name="FIM 3 Comment - comment redacted"
                data-testid={`fim3-comment-${index}-text`}
                role={canOnlyModerateThisPost ? 'button' : undefined}
                tabIndex={canOnlyModerateThisPost ? 0 : undefined}
                onClick={() => {
                  if (canOnlyModerateThisPost) {
                    setEditingState({ id: comment.id, value: comment.comment });
                  }
                }}
                onKeyDown={(e) => {
                  if (canOnlyModerateThisPost && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setEditingState({ id: comment.id, value: comment.comment });
                  }
                }}
              >
                {comment.comment}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
